import { useMemo } from 'react';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useSchedulingLocationInfo } from '../use-scheduling-location-info';

type AvailabilityMode = 'PROVIDER' | 'OPERATORY';

export const useGetIsScheduleDefinitionConfigured = (isEnabled: boolean) => {
  const { selectedLocationIds } = useSchedulingLocationInfo();

  // Fetch location settings to get availabilityLens for each location
  const locationSettingsQueries = SchedulerV3.Queries.useListLocationSettingsQueries(
    selectedLocationIds.map((locationId) => ({
      request: { locationIds: [locationId] },
      options: {
        enabled: !!locationId && isEnabled,
      },
    }))
  );

  // Extract availabilityLens for each location
  const availabilityLensByLocation = useMemo(() => {
    return selectedLocationIds.reduce((acc, locationId, index) => {
      const query = locationSettingsQueries[index];
      const lens = (query.data?.locationsSettings?.[locationId]?.availabilityLens || 'PROVIDER') as AvailabilityMode;
      acc[locationId] = lens;
      return acc;
    }, {} as Record<string, AvailabilityMode>);
  }, [locationSettingsQueries, selectedLocationIds]);

  // Fetch schedule definitions for each location with their respective availabilityLens
  const scheduleDefinitionsQueries = SchedulerV3.Queries.useListScheduleDefinitionsQueries(
    selectedLocationIds.map((locationId) => ({
      request: {
        locationId,
        availabilityLens: availabilityLensByLocation[locationId],
      },
      options: {
        enabled: !!locationId && isEnabled && !!availabilityLensByLocation[locationId],
        refetchOnMount: 'always',
      },
    }))
  );

  const locationWiseScheduleDefinitionStatus = useMemo<Record<string, boolean>>(() => {
    return selectedLocationIds.reduce((acc, locationId, index) => {
      const query = scheduleDefinitionsQueries[index];
      const lens = availabilityLensByLocation[selectedLocationIds[index]];

      if (lens === 'OPERATORY') {
        const operatoryGroups = query.data?.operatoryGroups ?? [];
        acc[locationId] = operatoryGroups.some((group) => (group.scheduleDefinitions?.length ?? 0) > 0);
        return acc;
      }

      const providerGroups = query.data?.providerGroups ?? [];
      acc[locationId] = providerGroups.some((group) => (group.scheduleDefinitions?.length ?? 0) > 0);
      return acc;
    }, {} as Record<string, boolean>);
  }, [scheduleDefinitionsQueries, availabilityLensByLocation, selectedLocationIds]);

  const hasAnyScheduleDefinition = useMemo(() => {
    return Object.values(locationWiseScheduleDefinitionStatus).some((hasDefinitions) => hasDefinitions);
  }, [locationWiseScheduleDefinitionStatus]);

  const isLoading =
    locationSettingsQueries.some((query) => query.isLoading) ||
    scheduleDefinitionsQueries.some((query) => query.isLoading);

  return {
    isLoading,
    hasAnyScheduleDefinition,
    locationWiseScheduleDefinitionStatus,
  };
};
